import { forwardRef } from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn } from "../lib/cn";

export interface RadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {}

/** Vertical by default; `data-orientation` flips the flex direction. */
export const RadioGroup = forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      ref={ref}
      className={cn(
        "flex flex-col gap-2.5 data-[orientation=horizontal]:flex-row",
        className,
      )}
      {...props}
    />
  );
});
RadioGroup.displayName = "RadioGroup";

export interface RadioProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  /** Error state — red border + aria-invalid. */
  invalid?: boolean;
}

/** 20px radio circle with a bg-surface-primary dot when checked. */
export const Radio = forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Item>,
  RadioProps
>(({ className, invalid = false, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      aria-invalid={invalid ? true : undefined}
      className={cn(
        // Base: 20px circle, strong border
        "inline-flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-border-control bg-surface-default outline-none transition-colors duration-150",
        // Focus
        "focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-offset-1 focus-visible:ring-offset-surface-default",
        // Checked
        "data-[state=checked]:border-surface-primary",
        // Disabled
        "disabled:cursor-not-allowed disabled:opacity-38",
        invalid &&
          "border-surface-error data-[state=checked]:border-surface-error",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <div className="size-2.5 rounded-full bg-surface-primary" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
Radio.displayName = "Radio";
